<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search + Block Button -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model.trim="inputBlock" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search or add blocked domains / IPs..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" v-on:keyup.enter="onEnter" :disabled="isSubmitting" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn btn-danger text-light btn-sm"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap;"
                  @click="sendData" :disabled="isSubmitting">
                  <span v-if="isSubmitting">
                     <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Blocking...
                  </span>
                  <span v-else>
                     <i class="fa-solid fa-ban me-1"></i> Block Domain / IP
                  </span>
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                     </option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">Domain / IP</th>
                     <th scope="col" class="text-center">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0 && !isLoading">
                  <tr>
                     <td colspan="2">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(blockWebsite, index) in blockWebsites" :key="index">
                     <td>
                        <i class="fa-solid fa-ban me-2" style="color: #dc3545;"></i>
                        <span class="text-dark">{{ blockWebsite.domain }}</span>
                        <span v-if="blockWebsite.is_allowlisted || blockWebsite.is_whitelisted"
                              class="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 ms-2 px-2 py-1 align-middle"
                              style="font-size: 11px; font-weight: 600; border-radius: 6px;"
                              title="This domain is on the allowlist and takes precedence over the blocklist">
                           <i class="fa-solid fa-circle-check me-1"></i> On Allowlist
                        </span>
                     </td>
                     <td class="text-center">
                        <button type="button" class="ms-action-btn ms-action-btn--delete"
                            @click="deleteBlockList(index)"
                           title="Remove from block list">
                           <i class="fa fa-xmark"></i>
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce, IsValidIPV4, ExtractDomain, IsValidWildcard, IsValidCIDR, IsUniversalWildcard, extractErrorMessage, IsLocalhost } from '@/plugins/helper.js'
export default {

   components: {

   },

   data() {
      return {
         headers: [
            { text: "Domain", value: "domain" },
            { text: "Actions", value: "actions" },
         ],
         debounceFn: null,
         blockWebsites: [],
         inputBlock: "",
         isModalOpen: false,
         isLoading: false,
         isSubmitting: false,
         paginationData: {},
         pagination: {
            pageSize: 50,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
            { text: "Domain: A-Z", value: "domain__asc" },
            { text: "Domain: Z-A", value: "domain__desc" },
         ],
         sortBy: "domain__asc",
         sorting: {
            field: "domain",
            order: "asc"
         },
      };

   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },

      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() {
         this.getBlockList()
      },

      rowsPerPage() {
         this.pagination.page = 1
         this.getBlockList()
      },

      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getBlockList()
      },
   },

   mounted() {
      this.init();
   },

   created() {
      this.debounceFn = debounce(() =>
         this.getBlockList()
         , 500)
   },

   methods: {

      init() {
         this.getBlockList()
      },

      onSearch() {
         this.debounceFn()
      },

      previousPage() {
         this.pagination.page > 1 ? this.pagination.page -= 1 : ''
      },

      nextPage() {
         this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : ''
      },

      onEnter: function () {
         if (this.isSubmitting) return;
         this.sendData()
      },

      async sendData() {
         if (this.isSubmitting) return;
         try {
            if (this.inputBlock == null || this.inputBlock.trim() == "") {
               this.$toast.error("Please provide a valid domain or IP.")
               return
            }

            // Security guardrail: reject universal wildcard
            if (IsUniversalWildcard(this.inputBlock)) {
               this.$toast.error("Universal wildcard '*' is not permitted.")
               return
            }

            // Reject localhost
            if (IsLocalhost(this.inputBlock)) {
               this.$toast.error("Localhost is permanently permitted. It cannot be added to allow list or block list.")
               return
            }

            let domLen = this.inputBlock.length
            if (this.inputBlock[domLen - 1] == '/' && !this.inputBlock.includes('*')) {
               this.inputBlock = this.inputBlock.substring(0, domLen - 1)
            }

            let eDomain = ""

            let validIP = false;
            let validDomain = false;

            // Check for wildcard patterns (*.example.com, example.com/path/*)
            if (IsValidWildcard(this.inputBlock)) {
               eDomain = this.inputBlock
               validDomain = true
            // Check for CIDR notation (192.168.1.0/24)
            } else if (IsValidCIDR(this.inputBlock)) {
               eDomain = this.inputBlock
               validIP = true
            } else {
               validIP = IsValidIPV4(this.inputBlock)

               if (!validIP) {
                  eDomain = ExtractDomain(this.inputBlock)

                  if (eDomain) {
                     if (eDomain.includes("www.")) {
                        eDomain = eDomain.replace("www.", "")
                     }
                  }

                  validDomain = this.isValidDomain(eDomain)
               }
            }

            if (!validIP && !validDomain) {
               this.$toast.error("Please provide a valid domain or IP.")
               return
            }

            if (validIP) {
               eDomain = this.inputBlock
            }

            this.isSubmitting = true

            const payload = {
               domain: eDomain.toLowerCase()
            }

            const resp = await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/add-account-block-site", payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
               this.inputBlock = ""
               this.getBlockList()
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.inputBlock = ""
               this.getBlockList()
            }

          } catch (error) {
             console.log(error)
             this.$toast.error(extractErrorMessage(error, "Failed to add website to block list."))
             this.inputBlock = ""
             this.getBlockList()
          } finally {
             this.isSubmitting = false
          }
      },

      async getBlockList() {

         let apiPath = "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-account-block-list?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&sort_by=" + this.sorting.field
            apiPath += "&order_by=" + this.sorting.order
         }

         if (this.inputBlock !== "" || this.inputBlock !== null) {
            apiPath += "&search_key=" + this.inputBlock
         }

         this.isLoading = true
         await this.$axios.get(apiPath).
            then((response) => {
               if (response.data.block_list === "No Record Found")
                  this.blockWebsites = []
               else {
                  this.blockWebsites = response.data.block_list
               }

               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load block list."))
            })
            .finally(() => {
               this.isLoading = false
            })

      },

      async deleteBlockList(index) {
         try {
            const payload = {
               domain: this.blockWebsites[index].domain,
               id: this.blockWebsites[index].id
            }

            const resp = await this.$axios.delete("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/remove-account-block-site/" + this.blockWebsites[index].id, payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.getBlockList()
            }

         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to remove website from block list."))
         }
      },

      isValidDomain(domain) {
         const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
         return domainPattern.test(domain);
      },

      extractDomain(url) {
         try {
            if (this.isValidDomain(url))
               return url

            let parsedURL = new URL(url);
            let domain = parsedURL.hostname;
            return domain;
         } catch (err) {
            if (err) {
               console.log(err)
               return ""
            }
         }
      }
   }

}

</script>

<style>
/* Uses shared Spark styles: .filterTitle, .filterOptions, .bordered-select,
   .pagination, .pagination-label, .pagination-arrow, .mainTable
   defined in AllowList2.vue */
</style>